import React, { useState, useEffect } from "react";
import {
  Select,
  MenuItem,
  Typography,
  Box,
  TextField,
  Grid,
  Paper,
  FormControl,
  Tooltip,
  Autocomplete,
} from "@mui/material";
import { Map as MapIcon } from "@mui/icons-material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import moment from "moment";
import { Controller } from "react-hook-form";
import * as yup from "yup";
import { getAllEmirates } from "../../api/emirates";
import { getRoutesByEmirates } from "../../api/routes";
import { getBustopByRouteId } from "../../api/bustop";

const NewRequest = ({ control, errors, setValue }) => {
  const [emirates, setEmirates] = useState([]);
  const [busStops, setBusStops] = useState([]);
  const [routes, setRoutes] = useState([]);

  useEffect(() => {
    getAllEmirates()
      .then((data) => setEmirates(Array.isArray(data) ? data : data?.data || []))
      .catch((error) => console.error("Error fetching emirates:", error));
  }, []);

  const fetchRoutes = async (emirateId) => {
    if (setValue) {
      setValue("route", "");
      setValue("busStop", "");
    }
    setRoutes([]);
    setBusStops([]);
    if (!emirateId) return;

    try {
      const data = await getRoutesByEmirates(emirateId);
      if (data && data.status && Array.isArray(data.data)) {
        setRoutes(data.data);
      } else if (Array.isArray(data)) {
        setRoutes(data);
      } else {
        setRoutes([]);
      }
    } catch (error) {
      console.error("Error fetching routes:", error);
      setRoutes([]);
    }
  };

  const fetchBusStops = async (routeId) => {
    if (setValue) {
      setValue("busStop", "");
    }
    setBusStops([]);
    if (!routeId) return;

    try {
      const data = await getBustopByRouteId(routeId);
      if (data && data.status && Array.isArray(data.data)) {
        setBusStops(data.data);
      } else if (Array.isArray(data)) {
        setBusStops(data);
      } else {
        setBusStops([]);
      }
    } catch (error) {
      console.error("Error fetching bus stops:", error);
      setBusStops([]);
    }
  };

  return (
    <Box sx={{ width: "75%", margin: "auto", mt: 3 }}>
      <Paper elevation={0} sx={{ border: "1px solid black", p: 2 }}>
        <Box display="flex" alignItems="center" sx={{ bgcolor: "#e8e8e8", p: 1, mb: 2 }}>
          <Typography variant="subtitle1" component="div" sx={{ fontWeight: "bold" }}>
            Please provide the exact location for new transport: (NEW TRANSPORT REQUEST)
          </Typography>
        </Box>

        <Grid container spacing={2}>
          <Grid item xs={6}>
            <Typography variant="body2" component="label">
              Confirmed Date of Entry
            </Typography>
            <Controller
              name="confirmedDateOfEntry"
              control={control}
              render={({ field }) => (
                <LocalizationProvider dateAdapter={AdapterMoment}>
                  <DatePicker
                    {...field}
                    slotProps={{
                      textField: {
                        variant: "standard",
                        size: "small",
                        fullWidth: true,
                        error: !!errors.confirmedDateOfEntry,
                        helperText: errors.confirmedDateOfEntry?.message,
                        sx: {
                          "& .MuiInput-root": {
                            borderBottom: "1px solid black",
                            "&:before, &:after": { display: "none" },
                          },
                          "& input": {
                            fontSize: { xs: "0.6rem", md: "0.8rem", xl: "1.2rem" },
                            textAlign: "center",
                            marginTop: "-5px",
                          },
                        },
                      },
                    }}
                  />
                </LocalizationProvider>
              )}
            />
          </Grid>
          <Grid item xs={6}>
            <Typography variant="body2" component="label">
              Emirates
            </Typography>
            <Controller
              name="emirate"
              control={control}
              render={({ field }) => (
                <FormControl fullWidth variant="standard">
                  <Select
                    {...field}
                    onChange={(e) => {
                      field.onChange(e);
                      fetchRoutes(e.target.value);
                    }}
                    displayEmpty
                    error={!!errors.emirate}
                    inputProps={{ "aria-label": "Select emirate" }}
                    MenuProps={{ PaperProps: { sx: { maxHeight: 250 } } }}
                    sx={{
                      "&:before": { borderBottom: "1px solid black" },
                      "&:after": { borderBottom: "1px solid black" },
                    }}
                  >
                    <MenuItem value="" disabled>
                      Select emirate
                    </MenuItem>
                    {emirates?.map((emirate) => (
                      <MenuItem key={emirate?.eid} value={emirate?.eid}>
                        {emirate?.name}
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.emirate && (
                    <Typography color="error" variant="caption">
                      {errors.emirate.message}
                    </Typography>
                  )}
                </FormControl>
              )}
            />
          </Grid>
          <Grid item xs={6}>
            <Typography variant="body2" component="label">
              Route
            </Typography>
            <Controller
              name="route"
              control={control}
              render={({ field }) => (
                <Autocomplete
                  options={routes}
                  getOptionLabel={(option) =>
                    typeof option === "object" && option !== null
                      ? `${option.routename || ""} - ${option.routeno || ""}`
                      : ""
                  }
                  value={routes.find((r) => r.rid === field.value) || null}
                  onChange={(_, newValue) => {
                    const selectedVal = newValue ? newValue.rid : "";
                    field.onChange(selectedVal);
                    fetchBusStops(selectedVal);
                  }}
                  isOptionEqualToValue={(option, value) => option.rid === value.rid}
                  renderOption={(props, option) => (
                    <Box
                      component="li"
                      {...props}
                      key={option.rid}
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        fontSize: "0.9rem",
                      }}
                    >
                      <span>{`${option.routename} - ${option.routeno}`}</span>
                      {option.map && (
                        <Tooltip title="Show route on map" arrow>
                          <MapIcon
                            sx={{
                              marginLeft: "auto",
                              cursor: "pointer",
                              fontSize: "1.2rem",
                              color: "primary.main",
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              window.open(option.map, "_blank");
                            }}
                          />
                        </Tooltip>
                      )}
                    </Box>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="standard"
                      placeholder="Search and select route..."
                      error={!!errors.route}
                      helperText={errors.route?.message}
                      sx={{
                        "& .MuiInput-root": {
                          borderBottom: "1px solid black",
                          "&:before, &:after": { display: "none" },
                        },
                      }}
                    />
                  )}
                  ListboxProps={{
                    sx: { maxHeight: 250, overflowY: "auto" },
                  }}
                />
              )}
            />
          </Grid>
          <Grid item xs={6}>
            <Typography variant="body2" component="label">
              Pick-up / Drop-off Point{" "}
              <Box component="span" sx={{ fontSize: "0.75rem", fontStyle: "italic", color: "text.secondary" }}>
                (common pickup will be show in here, select the pickup near to your location)
              </Box>
            </Typography>
            <Controller
              name="busStop"
              control={control}
              render={({ field }) => (
                <Autocomplete
                  options={busStops}
                  getOptionLabel={(option) =>
                    typeof option === "object" && option !== null
                      ? `${option.pickupcode || ""} - ${option.bustopname || ""}`
                      : ""
                  }
                  value={busStops.find((s) => s.sid === field.value) || null}
                  onChange={(_, newValue) => {
                    field.onChange(newValue ? newValue.sid : "");
                  }}
                  isOptionEqualToValue={(option, value) => option.sid === value.sid}
                  renderOption={(props, option) => (
                    <Box component="li" {...props} key={option.sid} sx={{ fontSize: "0.9rem" }}>
                      {`${option.pickupcode} - ${option.bustopname}`}
                    </Box>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="standard"
                      placeholder="Search and select pick-up / drop-off point..."
                      error={!!errors.busStop}
                      helperText={errors.busStop?.message}
                      sx={{
                        "& .MuiInput-root": {
                          borderBottom: "1px solid black",
                          "&:before, &:after": { display: "none" },
                        },
                      }}
                    />
                  )}
                  ListboxProps={{
                    sx: { maxHeight: 250, overflowY: "auto" },
                  }}
                />
              )}
            />
          </Grid>
          <Grid item xs={12}>
            <Typography variant="body2" component="label">
              Address:
            </Typography>
            <Controller
              name="address"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  variant="standard"
                  error={!!errors.address}
                  helperText={errors.address?.message}
                  InputProps={{ disableUnderline: true }}
                  sx={{
                    "& .MuiInput-root": {
                      borderBottom: "1px solid black",
                      "&:before, &:after": { display: "none" },
                    },
                  }}
                />
              )}
            />
          </Grid>
        </Grid>

        <Typography variant="caption" sx={{ mt: 2, display: "block" }}>
          Note:- Only limited areas are covered, kindly visit Al Saraya website for more information
          - www.alsaraya.ae
        </Typography>
      </Paper>
    </Box>
  );
};

NewRequest.validationSchema = yup.object().shape({
  confirmedDateOfEntry: yup.date().required("Confirmed Date of Entry is required"),
  emirate: yup.string().required("Emirates is required"),
  route: yup.string().required("Route is required"),
  busStop: yup.string().required("Pick-up / Drop-off Point is required"),
  address: yup.string().required("Address is required"),
});

export default NewRequest;
